import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart3,
  Phone,
  Clock,
  Users,
  TrendingUp,
  Loader2,
  FileText,
  Filter,
  AlertTriangle,
  CheckCircle2,
  Download
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Reports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState("this_month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/reports/productivity?date_range=${dateRange}`;
      if (dateRange === "custom" && fromDate && toDate) {
        url = `${API}/reports/productivity?date_range=custom&from_date=${fromDate}&to_date=${toDate}`;
      }
      const response = await axios.get(url);
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      toast.error("Failed to load reports");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange === "custom") {
      if (fromDate && toDate) fetchReportData();
    } else {
      fetchReportData();
    }
  }, [dateRange, fromDate, toDate]);

  const exportToCSV = () => {
    if (!reportData?.agent_performance?.length) {
      toast.error("No data to export");
      return;
    }
    const headers = ["Agent", "Email", "Total Leads", "Call Attempted", "Pending", "Pick Pool", "No. of Calls", "Talk Time (min)", "FU Missed"];
    const rows = reportData.agent_performance.map(a => [
      a.name,
      a.email,
      a.leads_assigned,
      a.calls_attempted || 0,
      a.pending_leads || 0,
      a.pick_pool_pushed || 0,
      a.calls_made,
      Math.round((a.total_talk_time || 0) / 60),
      a.followups_missed || 0
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agent_performance_${dateRange}${dateRange === "custom" ? `_${fromDate}_${toDate}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reports
          </h1>
          <p className="text-slate-500 mt-1">Agent productivity & lead analysis</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]" data-testid="date-range-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[150px] h-9"
                data-testid="from-date-input"
              />
              <span className="text-slate-400 text-sm">to</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[150px] h-9"
                data-testid="to-date-input"
              />
            </div>
          )}
          <Button variant="outline" onClick={fetchReportData} data-testid="refresh-reports-btn">
            <TrendingUp className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV} data-testid="export-report-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {formatNumber(reportData?.summary?.total_leads)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {formatNumber(reportData?.summary?.total_calls)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Talk Time</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {formatDuration(reportData?.summary?.total_talk_time)}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Agents</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {formatNumber(reportData?.summary?.active_agents)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Agent Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Agent</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Total Leads</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Call Attempted</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Pending</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Pick Pool</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">No. of Calls</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Talk Time</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">FU Missed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {reportData?.agent_performance?.length > 0 ? (
                  reportData.agent_performance.map((agent) => (
                    <tr key={agent.user_id} className="hover:bg-slate-50" data-testid={`agent-row-${agent.user_id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-900 font-medium text-sm">
                              {agent.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{agent.name}</p>
                            <p className="text-xs text-slate-500">{agent.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-slate-900">{agent.leads_assigned}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-emerald-600">{agent.calls_attempted || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-semibold ${agent.pending_leads > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                          {agent.pending_leads || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-green-600">{agent.pick_pool_pushed || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-slate-900">{agent.calls_made}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-blue-600">{formatDuration(agent.total_talk_time)}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-semibold ${agent.followups_missed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          {agent.followups_missed || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-400">
                      No agent data available for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Category & Channel Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category-wise Queries */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Leads by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData?.category_breakdown?.length > 0 ? (
                reportData.category_breakdown.map((item) => (
                  <div key={item.category} className="flex items-center justify-between" data-testid={`category-${item.category}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-slate-700">{item.category || "Uncategorized"}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${(item.count / (reportData?.summary?.total_leads || 1)) * 100}%` }}
                        ></div>
                      </div>
                      <span className="font-semibold text-slate-900 w-12 text-right">{item.count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-400 py-4">No category data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Channel-wise Leads */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Leads by Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData?.channel_breakdown?.length > 0 ? (
                reportData.channel_breakdown.map((item) => (
                  <div key={item.channel} className="flex items-center justify-between" data-testid={`channel-${item.channel}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        item.channel === 'website' ? 'bg-emerald-500' :
                        item.channel === 'meta' ? 'bg-blue-500' :
                        item.channel === 'outbound_calling' ? 'bg-orange-500' :
                        item.channel === 'referral' ? 'bg-purple-500' :
                        'bg-slate-400'
                      }`}></div>
                      <span className="text-slate-700 capitalize">{item.channel?.replace('_', ' ') || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-slate-100 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            item.channel === 'website' ? 'bg-emerald-500' :
                            item.channel === 'meta' ? 'bg-blue-500' :
                            item.channel === 'outbound_calling' ? 'bg-orange-500' :
                            item.channel === 'referral' ? 'bg-purple-500' :
                            'bg-slate-400'
                          }`}
                          style={{ width: `${(item.count / (reportData?.summary?.total_leads || 1)) * 100}%` }}
                        ></div>
                      </div>
                      <span className="font-semibold text-slate-900 w-12 text-right">{item.count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-400 py-4">No channel data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
